import React, { FC, useContext, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Hidden from '@mui/material/Hidden';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { CurrencyContext } from 'context/currency';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import PatientLabStep from 'components/order-flow/simple-order/patient-lab-step/patient-lab-step.component';
import ConstructionsStep from 'components/order-flow/simple-order/constructions-step/constructions-step.component';
import DeliveryFilesStep from 'components/order-flow/simple-order/delivery-files-step/delivery-files-step.component';
import ConfirmStep from 'components/order-flow/simple-order/confirm-step/confirm-step.component';
import { SimpleOrderContext } from './simple-order-context';

const SimpleOrder: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const [currency, setCurrency] = useState('');
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();
    const [activeStep, setActiveStep] = useState(0);
    const { order } = useContext(SimpleOrderContext);
    const params = useParams<{ id: string }>();

    const getSteps = () => {
        return [
            translate('PATIENT_LAB'),
            translate('CONSTRUCTIONS_FILES'),
            translate('DELIVERY'),
            translate('CONFIRM'),
        ];
    };

    useEffect(() => {
        const currencyFromSettings = customerSettings.getCustomerSetting(
            currentOrganization.customerSettings,
            CONSTANTS.SETTINGS_NAMES.CURRENCY,
        );
        setCurrency(currencyFromSettings);
        setActiveStep(0);
    }, []);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        // For lab user - If we are modifying an order, skip the first step
        if (params.id && permissions.isLabTechnician(currentUser.type) && order?._id && activeStep === 0) {
            setActiveStep(1);
        }
    }, [activeStep]);

    const getStepContent = () => {
        switch (activeStep) {
            case 0:
                return <PatientLabStep onNextClick={handleNext} />;
            case 1:
                return <ConstructionsStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 2:
                return <DeliveryFilesStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 3:
                return <ConfirmStep />;
            default:
                return 'Unknown stepIndex';
        }
    };

    const steps = getSteps();

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStepperClick = (step: number) => {
        if (step < activeStep) {
            setActiveStep(step);
        }
    };

    return (
        <Box sx={{ maxWidth: '1200px' }} display="flex" flexDirection="column" mx="auto">
            <CurrencyContext.Provider
                value={{
                    currency,
                    setCurrency: (currency: string) => {
                        setCurrency(currency);
                    },
                }}
            >
                <Box mb={2}>
                    <Hidden mdDown>
                        <Stepper activeStep={activeStep}>
                            {steps.map((label, index) => (
                                <Step
                                    sx={{ cursor: 'pointer' }}
                                    key={label}
                                    onClick={() => handleStepperClick(index)}
                                    data-test={`simple-order.step-${index}`}
                                >
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Hidden>
                </Box>
                <Paper elevation={1}>
                    <Box p={3}>{getStepContent()}</Box>
                </Paper>
            </CurrencyContext.Provider>
        </Box>
    );
};

export default SimpleOrder;
